import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	PrinterForm,
	PrinterList,
	PrintQueueItemDetail,
	PrintQueueRow,
} from "./models";

const moment = getMomentTz();

// DB datetimes are UTC: they become Date here and screens format them in server
// time; a Date sent back goes out as toISOString()
const utc = (v: string | null) => (v ? moment.utc(v).toDate() : null);

const call = async (sp: string, parameters: spParameter[] = []) => {
	const resp = await new MESApiService().callV2(sp, parameters);
	if (!resp.ok) throw new Error(resp.message || `Error calling ${sp}`);
	return resp;
};

export const getPrinters = async (): Promise<PrinterList> => {
	const resp = await call("[PRT].[GetPrinters]");
	return {
		printers: get(resp, "data.tables[0].rows", []).map((r: any) => ({
			...r,
			LastContactAt: utc(r.LastContactAt),
			LastPrintedAt: utc(r.LastPrintedAt),
		})),
		stations: get(resp, "data.tables[1].rows", []),
	};
};

export const upsertPrinter = async (f: PrinterForm) => {
	await call("[PRT].[UpsertPrinter]", [
		{ name: "PrinterId", value: f.PrinterId },
		{ name: "AssetId", value: f.AssetId },
		{ name: "PrinterName", value: f.PrinterName },
		{ name: "IpAddress", value: f.IpAddress },
		{ name: "Port", value: Number(f.Port) },
		{ name: "IsActive", value: f.IsActive },
		{ name: "Driver", value: f.Driver },
	]);
};

export const getPrintQueue = async (
	printerId: number,
): Promise<PrintQueueRow[]> => {
	const resp = await call("[PRT].[GetPrintQueue]", [
		{ name: "PrinterId", value: printerId },
	]);
	return get(resp, "data.tables[0].rows", []).map((r: any) => ({
		...r,
		QueuedAt: utc(r.QueuedAt),
		SentAt: utc(r.SentAt),
		PrintedAt: utc(r.PrintedAt),
	}));
};

export const getPrintQueueItem = async (
	printQueueItemId: number,
): Promise<PrintQueueItemDetail> => {
	const resp = await call("[PRT].[GetPrintQueueItem]", [
		{ name: "PrintQueueItemId", value: printQueueItemId },
	]);
	return {
		item: get(resp, "data.tables[0].rows[0]", null),
		log: get(resp, "data.tables[1].rows", []).map((r: any) => ({
			...r,
			LoggedAt: utc(r.LoggedAt),
		})),
	};
};

export const cancelPrintQueueItem = async (printQueueItemId: number) => {
	await call("[PRT].[CancelPrintQueueItem]", [
		{ name: "PrintQueueItemId", value: printQueueItemId },
	]);
};

const printed = async (sp: string, parameters: spParameter[]) => ({
	printQueueItemId: get(
		await call(sp, parameters),
		"data.tables[0].rows[0].PrintQueueItemId",
	) as number,
});

export const printTestTag = (printerId: number) =>
	printed("[PRT].[PrintTestTag]", [{ name: "PrinterId", value: printerId }]);

export const reprintPrintQueueItem = (printQueueItemId: number) =>
	printed("[PRT].[ReprintPrintQueueItem]", [
		{ name: "PrintQueueItemId", value: printQueueItemId },
	]);
